import {
  CLOCK_CX,
  CLOCK_CY,
  DAY_NAMES,
  MONTH_NAMES,
} from './DatePicker.constants';

// ─────────────────────────────────────────────────────────────────────────────
// Clock geometry
// ─────────────────────────────────────────────────────────────────────────────

/** Compute (x, y) position of a clock label on the face circle. */
export const clockPos = (
  index: number,
  total: number,
  radius: number,
): {x: number; y: number} => {
  const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
  return {
    x: CLOCK_CX + radius * Math.cos(angle),
    y: CLOCK_CY + radius * Math.sin(angle),
  };
};

/**
 * Resolve a touch location on the clock SVG to a selected value.
 * Returns null if the touch is too close to the centre (dead zone).
 */
export const resolveClockTouch = (
  locationX: number,
  locationY: number,
  isHourMode: boolean,
): number | null => {
  const dx = locationX - CLOCK_CX;
  const dy = locationY - CLOCK_CY;
  if (Math.sqrt(dx * dx + dy * dy) < 20) return null;
  let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
  if (deg < 0) deg += 360;
  if (isHourMode) {
    const h = Math.round(deg / 30) % 12;
    return h === 0 ? 12 : h;
  }
  return Math.round(deg / 6) % 60;
};

// ─────────────────────────────────────────────────────────────────────────────
// Date / time formatters
// ─────────────────────────────────────────────────────────────────────────────

/** Format ISO date string (YYYY-MM-DD) into material header labels. */
export const formatSelectedDate = (iso: string): {year: string; label: string} => {
  if (!iso) return {year: '----', label: 'Select a date'};
  const d = new Date(iso + 'T00:00:00');
  return {
    year: String(d.getFullYear()),
    label: `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`,
  };
};

/** Zero-pad a number to 2 digits. */
export const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Build the final date(-time) string to emit to the parent.
 * When withTime is false returns "MM/DD/YYYY"; when true "MM/DD/YYYY HH:MM AM/PM".
 */
export const buildDateValue = (
  selected: string,
  hour: number,
  minute: number,
  ampm: 'AM' | 'PM',
  withTime: boolean,
): string => {
  const [year, mon, day] = selected.split('-');
  if (!withTime) return `${mon}/${day}/${year}`;
  return `${mon}/${day}/${year} ${pad2(hour)}:${pad2(minute)} ${ampm}`;
};

/** Clamp and parse hour text input (valid range 1–12). */
export const parseHourInput = (text: string, currentHour: number): number => {
  const n = parseInt(text, 10);
  if (isNaN(n)) return currentHour;
  return Math.min(12, Math.max(1, n));
};

/** Clamp and parse minute text input (valid range 0–59). */
export const parseMinuteInput = (text: string, currentMinute: number): number => {
  const n = parseInt(text, 10);
  if (isNaN(n)) return currentMinute;
  return Math.min(59, Math.max(0, n));
};